import { parseStreamFrame } from "@typedoc/contracts/chat";
import { applyFrame, type ChatState } from "@typedoc/contracts/chat/reducer";
import { normalizeAppError, type AppErrorCode } from "../app-error";
import type { Held } from "./live-map";

/**
 * Follows one chat's stream for as long as the page is on it: the chat as a snapshot plus the
 * events after it, kept equal to the chat on the server (the reducer's rule). It is the part of the
 * live client that has no network in it, so what it does when the stream ends, is reset, or breaks
 * can be said in tests.
 *
 * - A stream ends within 55 seconds, so an ending is the normal case: reconnect from the cursor the
 *   last batch covered, which `applyFrame` moves even for a batch emptied for a reader.
 * - A `reset` frame, a frame this client cannot read, and an event that names something not held
 *   (a gap) all mean the held chat cannot be trusted: fetch the snapshot again and go on from it.
 *   The fresh snapshot replaces what is held even when its cursor is behind, which is what a cursor
 *   from the future needs.
 * - A failure is waited out with a growing delay and the same cursor. A chat that is not there
 *   (unshared, removed) ends it with `null`. A failure that means the person, workspace or token
 *   has changed ends it for good, because trying again would ask as someone else; the exception is
 *   a token cache that was only cleared, where nobody else holds it and asking again is safe.
 */
export type FollowDeps = {
  /** The chat as the server holds it now: the newest page, its in-flight events and the older pages. */
  load(signal: AbortSignal): Promise<Held>;
  /** The stream's frames after `after`, as they arrive. Ends when the server ends the stream. */
  frames(after: number, signal: AbortSignal): Promise<AsyncIterable<unknown>>;
  /** What is held for the chat now, which the page's own fetches may have moved on. */
  read(): Held | null;
  /** Holds this chat, whatever was held before. */
  replace(held: Held): void;
  /** Waits `ms`, or until `signal` is aborted. */
  wait(ms: number, signal: AbortSignal): Promise<void>;
  now(): number;
  /**
   * Whether the last failure was a token cache being cleared under it and nobody else holds the
   * cache now, so asking for a token again is safe and can help. The account dialog clears it on
   * opening and on closing. A cache that has moved to another scope is not such a case, and nor is
   * a token that was refused or named another workspace, which asking again does not change.
   */
  retryable(): boolean;
};

const RETRY_BASE_MS = 500;
const RETRY_MAX_MS = 15_000;
/** A stream that ends sooner than this having said nothing was refused, not finished. */
const SHORTEST_STREAM_MS = 2_000;
/** However a stream ended, the next is not asked for sooner than this after the last was. */
const SHORTEST_GAP_MS = 250;

/** How long to wait before the `attempt`th try in a row (1 is the first). */
export const retryDelay = (attempt: number): number =>
  Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), RETRY_MAX_MS);

/** A token cache that was cleared aborts what was under way with these, and may be asked again. */
const CLEARED: ReadonlySet<AppErrorCode> = new Set(["ACCESS_CHANGED", "REQUEST_CANCELLED"]);

/** Failures that mean who is asking, or where, has changed: trying again would ask as someone else. */
const FOR_GOOD: ReadonlySet<AppErrorCode> = new Set([
  "ACCESS_CHANGED",
  "REQUEST_CANCELLED",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "WORKSPACE_REQUIRED",
  "REGION_UNAVAILABLE",
]);

/** Whether anything the page shows differs; the cursor alone does not. */
function differs(before: ChatState, after: ChatState): boolean {
  const a = before.snapshot;
  const b = after.snapshot;
  return (
    a.messages !== b.messages ||
    a.queue !== b.queue ||
    a.run !== b.run ||
    a.requests !== b.requests ||
    a.chat !== b.chat
  );
}

/**
 * Yields the chat each time the stream changes what the page shows, and `null` once it is not there
 * for this person any more. It ends when `signal` aborts or on a failure that cannot be retried.
 */
export async function* follow(
  deps: FollowDeps,
  signal: AbortSignal,
): AsyncGenerator<Held | null, void> {
  let failures = 0;
  let reloads = 0;
  while (!signal.aborted) {
    try {
      let current = deps.read();
      if (!current) {
        current = await deps.load(signal);
        deps.replace(current);
        yield current;
      }
      const startedAt = deps.now();
      let heard = false;
      let untrusted = false;
      const frames = await deps.frames(current.state.lastSeq, signal);
      for await (const raw of frames) {
        const parsed = parseStreamFrame(raw);
        if (!parsed || parsed.frame.kind === "reset") {
          untrusted = true;
          break;
        }
        const held = deps.read() ?? current;
        const applied = applyFrame(held.state, parsed.frame);
        if (applied.gap) {
          untrusted = true;
          break;
        }
        heard = true;
        failures = 0;
        reloads = 0;
        if (applied.state === held.state) continue;
        const next: Held = { state: applied.state, older: held.older };
        deps.replace(next);
        if (differs(held.state, applied.state)) yield next;
      }
      if (signal.aborted) return;
      if (untrusted) {
        reloads += 1;
        // The first reload is a stale cursor doing its job; a second in a row is something wrong.
        if (reloads > 1) await deps.wait(retryDelay(reloads - 1), signal);
        if (signal.aborted) return;
        const fresh = await deps.load(signal);
        deps.replace(fresh);
        yield fresh;
      } else {
        const lasted = deps.now() - startedAt;
        if (!heard && lasted < SHORTEST_STREAM_MS) {
          failures += 1;
          await deps.wait(retryDelay(failures), signal);
        } else if (lasted < SHORTEST_GAP_MS) {
          await deps.wait(SHORTEST_GAP_MS - lasted, signal);
        }
      }
    } catch (error) {
      if (signal.aborted) return;
      const failure = normalizeAppError(error, { fallbackCode: "NETWORK_ERROR" });
      if (failure.code === "NOT_FOUND") {
        yield null;
        return;
      }
      if (FOR_GOOD.has(failure.code) && !(CLEARED.has(failure.code) && deps.retryable())) return;
      failures += 1;
      await deps.wait(retryDelay(failures), signal);
    }
  }
}
